import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../core/auth/useAuth'
import { homePathFor } from '../../core/router/paths'
import { ApiError } from '../../core/api/types'
import { useI18n } from '@/core/i18n/useI18n'
import { hubDef, lembrarHub, useHubLembrado, type HubKey } from '@/core/hubs/hubs'
import { HeroDoHub, MarcaDoHub, RodapeDoHub } from './hubs/MolduraDoHub'
import { PELES } from './hubs/peles'
import { SeletorDeHub } from './hubs/SeletorDeHub'
import './login.css'

type FormValues = { email: string; senha: string; remember?: boolean }

type Traduzir = (key: string, params?: Record<string, string | number>) => string

function messageFor(err: unknown, t: Traduzir, hub: HubKey): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      // A senha conferiu, mas a conta não alcança o hub escolhido. O seletor
      // continua ativo para a pessoa trocar de porta.
      case 'HUB_SEM_ACESSO':
        return t('auth.loginPage.errHubSemAcesso', { hub: hubDef(hub).nome })
      case 'INVALID_CREDENTIALS':
        return t('auth.loginPage.errInvalidCredentials')
      case 'ACCOUNT_BLOCKED':
        return t('auth.loginPage.errAccountBlocked')
      case 'ACCOUNT_NOT_ACTIVATED':
        return t('auth.loginPage.errAccountNotActivated')
      case 'NETWORK_ERROR':
        return t('auth.loginPage.errNetwork')
      case 'VALIDATION_ERROR':
        return t('auth.loginPage.errValidation')
      default:
        return err.message || t('auth.loginPage.errGeneric')
    }
  }
  return t('auth.loginPage.errGeneric')
}

export function LoginPage() {
  const { t } = useI18n()
  const { login, completarMfa, status, user } = useAuth()
  const navigate = useNavigate()
  const [showPwd, setShowPwd] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  // Desafio pendente do segundo fator. Enquanto existir, a tela pede o código
  // em vez da senha. Nenhum token foi guardado até aqui.
  const [desafio, setDesafio] = useState<string | null>(null)
  const [codigo, setCodigo] = useState('')
  const [verificando, setVerificando] = useState(false)
  const hub = useHubLembrado()

  const schema = z.object({
    email: z.string().min(1, t('auth.loginPage.emailRequired')).email(t('auth.loginPage.emailInvalid')),
    senha: z.string().min(1, t('auth.loginPage.passwordRequired')),
    remember: z.boolean().optional(),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { email: '', senha: '', remember: true } })

  if (status === 'authenticated') {
    // Sessão parcial vai direto para a criação da senha, sem passar pela home só
    // para ser devolvida de lá pelo guard.
    if (user?.senha_temporaria) return <Navigate to="/primeiro-acesso" replace />
    return <Navigate to={homePathFor(user)} replace />
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null)
    try {
      const resultado = await login({ email: values.email, senha: values.senha, hub })
      // Com segundo fator ativo, a senha correta ainda não entrou: o servidor
      // devolveu um desafio, e nenhum token foi guardado.
      if (resultado.tipo === 'mfa') {
        setDesafio(resultado.desafio)
        setCodigo('')
        return
      }
      if (resultado.senhaTemporaria) {
        navigate('/primeiro-acesso', { replace: true })
        return
      }
      navigate(homePathFor(resultado.usuario), { replace: true })
    } catch (err) {
      setServerError(messageFor(err, t, hub))
    }
  })

  const onVerificarCodigo = async (evento: React.FormEvent) => {
    evento.preventDefault()
    if (!desafio || verificando) return
    setServerError(null)
    setVerificando(true)
    try {
      const usuario = await completarMfa(desafio, codigo)
      if (usuario.senha_temporaria) {
        navigate('/primeiro-acesso', { replace: true })
        return
      }
      navigate(homePathFor(usuario), { replace: true })
    } catch (err) {
      setServerError(messageFor(err, t, hub))
      setCodigo('')
    } finally {
      setVerificando(false)
    }
  }

  const voltarParaSenha = () => {
    setDesafio(null)
    setCodigo('')
    setServerError(null)
  }

  // A mensagem do servidor fala do hub de antes; trocar a porta a apaga.
  const trocarHub = (novo: HubKey) => {
    if (novo === hub) return
    lembrarHub(novo)
    setServerError(null)
  }

  const firstFieldError = errors.email?.message ?? errors.senha?.message
  const errorText = serverError ?? firstFieldError

  return (
    <main className="login-stage" data-hub={hub}>
      <HeroDoHub hub={hub} />

      <section className="login-form">
        <svg className="login-wm" viewBox="0 0 588 289" aria-hidden="true">
          <use href="#leaf" />
        </svg>

        <SeletorDeHub hub={hub} onTrocar={trocarHub} desabilitado={isSubmitting || verificando || Boolean(desafio)} />
        <MarcaDoHub hub={hub} />

        <div className="login-body">
          <div className="login-welcome">
            <h1>
              <svg className="login-bullet" viewBox="0 0 588 289" aria-hidden="true">
                <use href="#leaf" />
              </svg>
              {t('auth.loginPage.welcomeTitle')}
            </h1>
            <p>{t('auth.loginPage.welcomeSubtitle')}</p>
          </div>

          {desafio ? (
            <form className="login-fields" onSubmit={onVerificarCodigo} noValidate>
              <div className="login-field">
                <label htmlFor="mfa-codigo">{t('auth.loginPage.mfaCodeLabel')}</label>
                <div className="login-input">
                  <span className="login-ico" aria-hidden="true">
                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="11" width="16" height="10" rx="2.5" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      <circle cx="12" cy="16" r="1" />
                    </svg>
                  </span>
                  <input
                    id="mfa-codigo"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    placeholder="000000"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                  />
                </div>
                <p className="login-hint">{t('auth.loginPage.mfaHint')}</p>
              </div>

              {errorText && (
                <div className="login-error" role="alert">
                  {errorText}
                </div>
              )}

              <button className="login-btn" type="submit" disabled={verificando || !codigo.trim()}>
                {verificando ? (
                  <>
                    <span className="login-spinner" aria-hidden="true" />
                    {t('auth.loginPage.verifying')}
                  </>
                ) : (
                  t('auth.loginPage.verify')
                )}
              </button>

              <button type="button" className="login-secondary" onClick={voltarParaSenha}>
                {t('auth.loginPage.useAnotherAccount')}
              </button>
            </form>
          ) : (
          <form className="login-fields" onSubmit={onSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="email">{t('auth.loginPage.emailLabel')}</label>
              <div className="login-input">
                <span className="login-ico" aria-hidden="true">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="3" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                </span>
                <input id="email" type="email" placeholder={t(PELES[hub].emailPlaceholderKey)} autoComplete="username" {...register('email')} />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="senha">{t('auth.loginPage.passwordLabel')}</label>
              <div className="login-input">
                <span className="login-ico" aria-hidden="true">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="11" width="16" height="10" rx="2.5" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                </span>
                <input
                  id="senha"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  {...register('senha')}
                />
                <button
                  type="button"
                  className="login-toggle"
                  aria-label={showPwd ? t('auth.loginPage.hidePassword') : t('auth.loginPage.showPassword')}
                  onClick={() => setShowPwd((v) => !v)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="login-row">
              <label className="login-remember">
                <input type="checkbox" {...register('remember')} /> {t('auth.loginPage.rememberMe')}
              </label>
              <Link className="login-forgot" to="/esqueci-senha">
                {t('auth.loginPage.forgotPassword')}
              </Link>
            </div>

            {errorText && (
              <div className="login-error" role="alert">
                {errorText}
              </div>
            )}

            <button className="login-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  {t('auth.loginPage.signingIn')}
                </>
              ) : (
                <>
                  {t('auth.loginPage.signIn')}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                </>
              )}
            </button>
          </form>
          )}
        </div>

        <RodapeDoHub hub={hub} />
      </section>
    </main>
  )
}
